import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { GetImageMetdataLink, StorageAPI } from '@makyoapp/makyo-sdk'
import { FileCategory, ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import { Close12 } from '@makyoapp/makyo-icon/icon'
import { Modal } from '@makyoapp/makyo-ui'

import { queryClient, queryKeys } from '@/lib/react-query'
import { toasted } from '@/lib/toast'
import { useStorageContext } from '@/context/StorageContext'

import StoragePreviewPDF from './preview/StoragePreviewPDF'

export function StoragePreview() {
  // Access context
  const { preview, setPreview, refreshStorage } = useStorageContext()
  const [isSaving, setIsSaving] = useState(false)
  const { t } = useTranslation()

  // Form state with default empty values
  const [altText, setAltText] = useState('')
  const [caption, setCaption] = useState('')
  const [description, setDescription] = useState('')

  // Log preview data for debugging
  useEffect(() => {
    if (preview) {
      // Initialize form fields from preview metadata
      setAltText(preview._Metadata?.alt || '')
      setCaption(preview._Metadata?.caption || '')
      setDescription(preview._Metadata?.description || '')
    }
  }, [preview])

  // Image source setup
  const isUnsplash = Boolean(preview?._Link?.includes('unsplash'))
  const largeSrc = isUnsplash
    ? `${preview?._Link}?q=50&w=720`
    : preview?._Link
      ? GetImageMetdataLink(preview?._Link, ImageMetadata.S)
      : undefined

  // Simple direct save function
  const handleSave = async () => {
    if (!preview?._id) return

    try {
      setIsSaving(true)

      const metadata = {
        alt: altText,
        caption: caption,
        description: description,
      }

      // Directly call API
      await StorageAPI.ReqUpdateMetadata(preview._id, metadata)

      // Force a query invalidation before closing
      try {
        const orgID = preview._Organization?._id
        if (orgID) {
          // Important: Use a more specific query key pattern to match the useGetStorage hook
          // This needs to match the exact format used in the hook
          await queryClient.invalidateQueries({
            queryKey: [queryKeys.orgStorage, orgID],
            // Force refresh of storage data
            refetchType: 'all', // Force refetch of all queries containing this key pattern
          })

          if (typeof refreshStorage === 'function') {
            await refreshStorage()
          }
        }
      } catch (error) {
        console.error('Error refreshing queries:', error)
      }

      // Update preview object for immediate UI feedback
      if (preview._Metadata) {
        preview._Metadata.alt = altText
        preview._Metadata.caption = caption
        preview._Metadata.description = description
      } else {
        preview._Metadata = {
          alt: altText,
          caption: caption,
          description: description,
        }
      }

      // Close modal
      setPreview(undefined)
    } catch (error) {
      // trigger toast error with generic message
      toasted(t('Unable to save details please try again later'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    setPreview(undefined)
  }

  return (
    <Modal show={preview !== undefined} onHide={handleClose}>
      <Modal.Body noPadding>
        <div className='flex h-full w-full'>
          {/* PDF Preview Section */}
          {preview?._Category === FileCategory.Document && preview?._Link?.toLowerCase().endsWith('.pdf') && (
            <StoragePreviewPDF fileUrl={preview._Link} fileName={preview._Name} onClose={handleClose} />
          )}

          {/* Image Preview Section */}
          {preview?._Category === FileCategory.Image && (
            <div
              className={clsx('relative flex items-center justify-center bg-black p-8', 'mk-img-full-preview modal-sm')}
              style={{
                backgroundImage: `url(${largeSrc})`,
              }}
            />
          )}

          {/* Video/Audio Preview Section */}
          {(preview?._Category === FileCategory.Video || preview?._Category === FileCategory.Audio) && (
            <div
              className={clsx(
                'relative flex items-center justify-center bg-black p-8',
                'mk-media-full-preview modal-md'
              )}
            />
          )}

          {/* Metadata Section */}
          {preview?._Category === FileCategory.Image && (
            <div className='w-1/2 bg-slate-800'>
              <div className='flex h-full flex-col'>
                <div className='flex items-center justify-between border-b border-slate-700 p-6'>
                  <h2 className='text-xl font-semibold text-white'>Image Metadata</h2>
                  <button className='rounded-full p-1 hover:bg-slate-700' onClick={handleClose}>
                    <Close12 />
                  </button>
                </div>

                <div className='flex-1 overflow-y-auto p-6'>
                  <div className='space-y-6'>
                    <div className='space-y-2'>
                      <label className='block text-sm font-medium text-slate-300'>Alt text</label>
                      <textarea
                        className='w-full rounded-lg bg-slate-900 p-3 text-sm text-white placeholder-slate-500 focus:outline-hidden focus:ring-2 focus:ring-emerald-500'
                        rows={3}
                        placeholder='Enter alt text for accessibility'
                        value={altText}
                        onChange={(e) => setAltText(e.target.value)}
                      />
                    </div>

                    <div className='space-y-2'>
                      <label className='block text-sm font-medium text-slate-300'>Caption</label>
                      <input
                        type='text'
                        className='w-full rounded-lg bg-slate-900 p-3 text-sm text-white placeholder-slate-500 focus:outline-hidden focus:ring-2 focus:ring-emerald-500'
                        placeholder='Enter image caption'
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                      />
                    </div>

                    <div className='space-y-2'>
                      <label className='block text-sm font-medium text-slate-300'>Description</label>
                      <textarea
                        className='w-full rounded-lg bg-slate-900 p-3 text-sm text-white placeholder-slate-500 focus:outline-hidden focus:ring-2 focus:ring-emerald-500'
                        rows={4}
                        placeholder='Enter detailed description'
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className='border-t border-slate-700 bg-slate-800 p-6'>
                  <button
                    className={clsx(
                      'w-full rounded-lg py-3 font-medium text-white transition-colors',
                      isSaving ? 'bg-slate-700 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600'
                    )}
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}
